import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaPlus,
  FaSpinner,
  FaBook,
  FaHeadphones,
  FaPencilAlt,
  FaLayerGroup,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";
import { TeilCard } from "./question/TeilCard";
import { ContextModal } from "./question/ContextModal";
import { QuestionModal } from "./question/QuestionModal";
import { CreateTeilModal } from "./question/CreateTeilModal";
import { HorenAudioUpload } from "./question/HorenAudioUpload";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminManageQuestions: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();

  // Data
  const [examSet, setExamSet] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [examParts, setExamParts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState("LESEN");
  const [expandedParts, setExpandedParts] = useState<Set<number>>(new Set());

  // Modals
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [showCreatePartModal, setShowCreatePartModal] = useState(false);

  // Selected
  const [selectedPart, setSelectedPart] = useState<any>(null);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);

  // Context
  const [contextText, setContextText] = useState("");
  const [contextImageFile, setContextImageFile] = useState<File | null>(null);
  const [contextDocumentFile, setContextDocumentFile] = useState<File | null>(
    null
  );
  const [contextAudioFile, setContextAudioFile] = useState<File | null>(null);
  const [uploadingContext, setUploadingContext] = useState(false);

  // Question form
  const [questionForm, setQuestionForm] = useState<any>({
    module: "LESEN",
    questionType: "MULTIPLE_CHOICE",
    questionText: "",
    contextText: "",
    options: ["", "", ""],
    correctAnswer: "",
    points: 1,
    examPartId: undefined,
  });
  const [questionImageFile, setQuestionImageFile] = useState<File | null>(null);

  useEffect(() => {
    fetchData();
  }, [examId]);

  useEffect(() => {
    console.log("🔍 Questions state updated:", questions);
    console.log("🔍 Questions count:", questions.length);
    console.log(
      "🔍 LESEN count:",
      questions.filter((q) => q.module === "LESEN").length
    );
  }, [questions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [examRes, partsRes, questionsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/exams/${examId}`, {
          credentials: "include",
        }),
        fetch(`${API_BASE_URL}/api/admin/exams/${examId}/parts`, {
          credentials: "include",
        }),
        fetch(`${API_BASE_URL}/api/admin/exams/${examId}/questions`, {
          credentials: "include",
        }),
      ]);

      if (examRes.ok) {
        const examData = await examRes.json();
        console.log("📘 Exam data:", examData);
        setExamSet(examData.data || examData);
      }

      if (partsRes.ok) {
        const partsData = await partsRes.json();
        console.log("📦 Parts data:", partsData);
        setExamParts(partsData.data || partsData || []);
      }

      if (questionsRes.ok) {
        const questionsData = await questionsRes.json();
        console.log("❓ Questions response:", questionsData);
        console.log("❓ Questions.data:", questionsData.data);
        console.log(
          "❓ Setting questions to:",
          questionsData.data || questionsData || []
        );
        setQuestions(questionsData.data || questionsData || []);
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const togglePart = (id: number) => {
    const n = new Set(expandedParts);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpandedParts(n);
  };

  const openContextModal = (part: any) => {
    setSelectedPart(part);
    setContextText(part.contextText || "");
    setContextImageFile(null);
    setContextDocumentFile(null);
    setContextAudioFile(null);
    setShowContextModal(true);
  };

  const saveContext = async () => {
    if (!selectedPart) return;
    setUploadingContext(true);

    try {
      if (contextText !== (selectedPart.contextText || "")) {
        await fetch(`${API_BASE_URL}/api/admin/exam-parts/${selectedPart.id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contextText }),
        });
      }

      const upload = async (file: File, endpoint: string) => {
        const fd = new FormData();
        fd.append("file", file);
        await fetch(
          `${API_BASE_URL}/api/admin/exam-parts/${selectedPart.id}/${endpoint}`,
          {
            method: "POST",
            credentials: "include",
            body: fd,
          }
        );
      };

      if (contextImageFile)
        await upload(contextImageFile, "upload-context-image");
      if (contextDocumentFile)
        await upload(contextDocumentFile, "upload-context-document");
      if (contextAudioFile)
        await upload(contextAudioFile, "upload-context-audio");

      await fetchData();
      setShowContextModal(false);
    } catch (err: any) {
      alert(err.message || "Failed");
    } finally {
      setUploadingContext(false);
    }
  };

  // Replace your addQuestion() function with this:

  const addQuestion = () => {
    const parts = examParts.filter((p) => p.module === activeModule);

    // Determine default question type based on module
    let defaultQuestionType = "MULTIPLE_CHOICE";
    if (activeModule === "SCHREIBEN") {
      defaultQuestionType = "EMAIL";
    } else if (activeModule === "HOREN") {
      defaultQuestionType = "AUDIO_MULTIPLE_CHOICE";
    }

    // Only set options if the default type needs them
    const needsOptions = [
      "MULTIPLE_CHOICE",
      "MULTIPLE_CHOICE_4",
      "AUDIO_MULTIPLE_CHOICE",
    ].includes(defaultQuestionType);

    setQuestionForm({
      module: activeModule,
      questionType: defaultQuestionType,
      questionText: "",
      contextText: "",
      options: needsOptions ? ["", "", ""] : [],
      correctAnswer: "",
      points: 1,
      examPartId: parts[0]?.id,
    });
    setEditingQuestion(null);
    setQuestionImageFile(null);
    setShowQuestionModal(true);
  };

  const editQuestion = (q: any) => {
    let opts = ["", "", ""];
    try {
      opts = typeof q.options === "string" ? JSON.parse(q.options) : q.options;
    } catch (e) {}

    setQuestionForm({
      module: q.module,
      questionType: q.questionType,
      questionText: q.questionText,
      contextText: q.contextText,
      options: opts,
      correctAnswer: q.correctAnswer,
      points: q.points,
      examPartId: q.examPartId,
    });
    setEditingQuestion(q);
    setQuestionImageFile(null);
    setShowQuestionModal(true);
  };

  const saveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionForm.examPartId) return alert("Select Teil");

    try {
      const fd = new FormData();
      fd.append("examSetId", examId!);
      fd.append("examPartId", questionForm.examPartId.toString());
      fd.append("module", questionForm.module);
      fd.append("questionType", questionForm.questionType);
      fd.append("questionText", questionForm.questionText || "");
      fd.append("contextText", questionForm.contextText || "");
      fd.append(
        "correctAnswer",
        questionForm.correctAnswer || "MANUALLY_GRADED"
      );
      fd.append("points", questionForm.points.toString());
      fd.append("partNumber", "1");
      fd.append(
        "questionNumber",
        (editingQuestion
          ? editingQuestion.questionNumber
          : questions.filter((q) => q.module === questionForm.module).length + 1
        ).toString()
      );

      if (questionForm.options && Array.isArray(questionForm.options)) {
        fd.append(
          "options",
          JSON.stringify(questionForm.options.filter((o: string) => o.trim()))
        );
      }

      if (questionImageFile) fd.append("imageFile", questionImageFile);

      const url = editingQuestion
        ? `${API_BASE_URL}/api/admin/exams/questions/${editingQuestion.id}/with-files`
        : `${API_BASE_URL}/api/admin/exams/questions/with-files`;

      const res = await fetch(url, {
        method: editingQuestion ? "PUT" : "POST",
        credentials: "include",
        body: fd,
      });

      if (res.ok) {
        await fetchData();
        setShowQuestionModal(false);
      } else {
        alert((await res.json()).message || "Failed");
      }
    } catch (err) {
      alert("Network error");
    }
  };

  const deleteQuestion = async (id: number) => {
    if (typeof window !== "undefined" && !window.confirm("Delete?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/admin/exams/questions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      await fetchData();
    } catch (err) {
      alert("Failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  const moduleQuestions = questions.filter((q) => q.module === activeModule);
  const availableParts = examParts.filter(
    (p) => p.module === questionForm.module
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaBook />}
        title="Manage Questions"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: "Questions" },
        ]}
      />

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <HorenAudioUpload examSet={examSet} onUploadComplete={fetchData} />

        {/* Module Tabs */}
        <div className="flex gap-3 mb-6">
          {[
            { m: "LESEN", icon: <FaBook />, c: "blue" },
            { m: "HOREN", icon: <FaHeadphones />, c: "purple" },
            { m: "SCHREIBEN", icon: <FaPencilAlt />, c: "green" },
          ].map(({ m, icon, c }) => (
            <button
              key={m}
              onClick={() => setActiveModule(m)}
              className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
                activeModule === m
                  ? `bg-${c}-600 text-white shadow-lg`
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {icon}
              {m.charAt(0) + m.slice(1).toLowerCase()} (
              {questions.filter((q) => q.module === m).length})
            </button>
          ))}
        </div>

        <button
          onClick={addQuestion}
          className="mb-6 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg flex items-center gap-2"
        >
          <FaPlus />
          Add Question to {activeModule}
        </button>

        {/* Teile */}
        <div className="space-y-6">
          {examParts
            .filter((p) => p.module === activeModule)
            .map((part) => (
              <TeilCard
                key={part.id}
                part={part}
                questions={moduleQuestions.filter(
                  (q) => q.examPartId === part.id
                )}
                isExpanded={expandedParts.has(part.id)}
                onToggle={() => togglePart(part.id)}
                onManageContext={() => openContextModal(part)}
                onEditQuestion={editQuestion}
                onDeleteQuestion={deleteQuestion}
              />
            ))}

          {examParts.filter((p) => p.module === activeModule).length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
              <FaLayerGroup className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Teile Yet
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Create your first Teil for {activeModule}
              </p>
              <button
                onClick={() => setShowCreatePartModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg flex items-center gap-2 mx-auto"
              >
                <FaPlus />
                Create First Teil
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate("/admin/exam-management")}
          className="mt-8 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
        >
          ← Back
        </button>
      </main>

      {/* Modals */}
      <ContextModal
        isOpen={showContextModal}
        part={selectedPart}
        contextText={contextText}
        imageFile={contextImageFile}
        documentFile={contextDocumentFile}
        audioFile={contextAudioFile}
        uploading={uploadingContext}
        onClose={() => setShowContextModal(false)}
        onSave={saveContext}
        onTextChange={setContextText}
        onImageChange={setContextImageFile}
        onDocumentChange={setContextDocumentFile}
        onAudioChange={setContextAudioFile}
      />

      <QuestionModal
        isOpen={showQuestionModal}
        isEditing={!!editingQuestion}
        formData={questionForm}
        availableParts={availableParts}
        imageFile={questionImageFile}
        onClose={() => setShowQuestionModal(false)}
        onSave={saveQuestion}
        onChange={(data) => setQuestionForm({ ...questionForm, ...data })}
        onImageChange={setQuestionImageFile}
        onCreatePart={() => {
          setShowQuestionModal(false);
          setShowCreatePartModal(true);
        }}
      />

      <CreateTeilModal
        isOpen={showCreatePartModal}
        module={activeModule}
        examId={examId!}
        onClose={() => setShowCreatePartModal(false)}
        onSuccess={() => {
          fetchData();
          setShowCreatePartModal(false);
        }}
      />
    </div>
  );
};

export default AdminManageQuestions;
